import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Typography, useTheme, InputBase, IconButton } from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import RouteIcon from "@mui/icons-material/Route";
import { API_BASE_URL } from "../../config";

const Contacts = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, page, search, limit: 20 }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data.data || []);
      setRowCount(data.total || (data.data ? data.data.length : 0));
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const columns = [
    { field: "id", headerName: "ID", flex: 0.8 },
    {
      field: "username",
      headerName: "Nom d'utilisateur",
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Typography fontWeight="600" color={colors.greenAccent[300]}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "phone",
      headerName: "Téléphone",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
    },
    {
      field: "total_rides",
      headerName: "Courses",
      flex: 0.6,
      renderCell: (params) => `${params.value || 0}`,
    },
    {
      field: "rating",
      headerName: "Note",
      flex: 0.6,
      renderCell: (params) => `⭐ ${params.value || "5.0"}`,
    },
    {
      field: "created_at",
      headerName: "Inscrit le",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[200]}>
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric"
          }) : "-"}
        </Typography>
      ),
    },
    {
      field: "rides_link",
      headerName: "Courses",
      flex: 0.8,
      renderCell: ({ row: { id } }) => (
        <Button
          component={Link}
          to={`/user/rides/${id}`}
          size="small"
          variant="contained"
          startIcon={<RouteIcon />}
          sx={{ backgroundColor: colors.blueAccent[600], fontSize: "11px" }}
        >
          Courses
        </Button>
      ),
    },
    {
      field: "edit",
      headerName: "Modifier",
      flex: 0.8,
      renderCell: ({ row: { id } }) => (
        <Button
          component={Link}
          to={`/edit/user/users/${id}`}
          size="small"
          variant="contained"
          startIcon={<EditIcon />}
          sx={{ backgroundColor: colors.greenAccent[600], fontSize: "11px" }}
        >
          Éditer
        </Button>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  }

  return (
    <Box m="20px">
      <Header title="PASSAGERS & CLIENTS" subtitle="Gestion des comptes utilisateurs enregistrés sur LinkerVTC" />

      <Box display="flex" backgroundColor={colors.primary[400]} borderRadius="3px" mt="15px">
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ml: 2, flex: 1 }}
          placeholder="Rechercher par nom, email ou téléphone..."
        />
        <IconButton type="button" sx={{ p: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <Box
        m="20px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
        }}
      >
        <DataGrid
          rows={users}
          columns={columns}
          slots={{ toolbar: CustomToolbar }}
          pagination
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          onPaginationModelChange={(newModel) => setPage(newModel.page + 1)}
        />
      </Box>
    </Box>
  );
};

export default Contacts;
